import React, { useState, useEffect, useRef } from 'react';
import './TransactionManagement.css';
import { jsPDF } from 'jspdf';
import autoTable, { HookData } from 'jspdf-autotable';
import vkslogo from './vkslogo.png';
interface Transaction { 
  id: number;
  username: string;
  name: string | null;
  type: 'credit' | 'debit_withdrawal' | 'debit_direct_id_creation' | 'debit_self_id_creation' | 'credit_autofill' | 'credit_direct_id' | 'credit_direct_self_id' | 'admin_fee';
  amount: number;
  status: 'pending' | 'completed' | 'failed';
  transaction_id: string | null;
  transaction_date: string;
  pan_number?: string | null;
  account_number?: string | null;
  ifsc_code?: string | null;
  mobile?: string | null;
  city?: string | null;
  referal_amount: number;
}

interface ConsolidatedTransaction {
  username: string;
  name: string | null;
  transaction_date: string;
  credit: number;
  debit_withdrawal: number;
  credit_autofill: number;
  admin_fee: number;
  debit_direct_id_creation: number;
  debit_self_id_creation: number;
  credit_direct_id: number;
  credit_direct_self_id: number;
}

interface GstData {
  username: string;
  name: string | null;
  id: number;
  transaction_date: string;
  amount: number;
  gst: number;
  quantity: number;
  invoice_number: number;
}

interface TdsData {
  id: number;
  username: string;
  name: string | null;
  transaction_date: string;
  pan_number: string | null;
  account_number: string | null;
  ifsc_code: string | null;
  bank_name: string | null;
  amount: number;
  tds: number;
}

type SortableKeys = keyof Transaction;
type ConsolidatedSortableKeys = keyof ConsolidatedTransaction;
type GstSortableKeys = keyof GstData;
type TdsSortableKeys = keyof TdsData;

const SEARCHABLE_TRANSACTION_KEYS: SortableKeys[] = [
  'id',
  'username',
  'name',
  'type',
  'amount',
  'status',
  'transaction_id',
  'transaction_date'
];

const getDisplayType = (type: Transaction['type']) => {
  switch (type) {
    case 'credit':
      return 'Credit';
    case 'debit_withdrawal':
      return 'Withdrawal';
    case 'debit_direct_id_creation':
      return 'Direct ID Creation';
    case 'debit_self_id_creation':
      return 'Self ID Creation';
    case 'credit_autofill':
      return 'Credit Autofill';
    case 'credit_direct_id':
      return 'Direct ID Credit';
    case 'credit_direct_self_id':
      return 'Self ID Credit';
    case 'admin_fee':
      return 'Admin Fee';
    default:
      const exhaustiveCheck: never = type;
      return exhaustiveCheck;
  }
};

const getDisplayTypeText = (type: Transaction['type']): string => {
  switch (type) {
    case 'credit':
      return 'Credit';
    case 'debit_withdrawal':
      return 'Withdrawal';
    case 'debit_direct_id_creation':
      return 'Direct ID Creation';
    case 'debit_self_id_creation':
      return 'Self ID Creation';
    case 'credit_autofill':
      return 'Credit Autofill';
    case 'credit_direct_id':
      return 'Direct ID Credit';
    case 'credit_direct_self_id':
      return 'Self ID Credit';
    case 'admin_fee':
      return 'Admin Fee';
    default:
      const exhaustiveCheck: never = type;
      // This part of the code should be unreachable with the current types
      return String(exhaustiveCheck);
  }
};

const toWords = (num: number): string => {
  const a = ['', 'one ', 'two ', 'three ', 'four ', 'five ', 'six ', 'seven ', 'eight ', 'nine ', 'ten ', 'eleven ', 'twelve ', 'thirteen ', 'fourteen ', 'fifteen ', 'sixteen ', 'seventeen ', 'eighteen ', 'nineteen '];
  const b = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];

  const inWords = (n: number): string => {
    let str = '';
    if (n > 99) {
      str += a[Math.floor(n / 100)] + 'hundred ';
      n %= 100;
    }
    if (n > 19) {
      str += b[Math.floor(n / 10)] + a[n % 10];
    } else {
      str += a[n];
    }
    return str;
  };

  if (num === 0) return 'zero';

  let words = '';
  if (num >= 10000000) { words += inWords(Math.floor(num / 10000000)) + 'crore '; num %= 10000000; }
  if (num >= 100000) { words += inWords(Math.floor(num / 100000)) + 'lakh '; num %= 100000; }
  if (num >= 1000) { words += inWords(Math.floor(num / 1000)) + 'thousand '; num %= 1000; }
  if (num > 0) { words += inWords(Math.floor(num)); }

  const amountInWords = words.trim().replace(/\s+/g, ' ');
  
  // Capitalize the first letter
  return amountInWords.charAt(0).toUpperCase() + amountInWords.slice(1) + ' only.';
};



const TransactionManagement: React.FC = () => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [consolidatedTransactions, setConsolidatedTransactions] = useState<ConsolidatedTransaction[]>([]);
  const [gstData, setGstData] = useState<GstData[]>([]);
  const [tdsData, setTdsData] = useState<TdsData[]>([]);
  const [viewMode, setViewMode] = useState<'individual' | 'consolidated' | 'gst' | 'tds'>('individual');

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<'all' | 'credit' | 'debit_withdrawal' | 'debit_direct_id_creation' | 'debit_self_id_creation' | 'credit_autofill' | 'credit_direct_id' | 'credit_direct_self_id' | 'admin_fee'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'completed' | 'failed'>('pending');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sortConfig, setSortConfig] = useState<{ key: SortableKeys | ConsolidatedSortableKeys | GstSortableKeys | TdsSortableKeys; direction: 'ascending' | 'descending' } | null>({ key: 'transaction_date', direction: 'descending' });
  const [searchableColumns, setSearchableColumns] = useState({
    id: true,
    username: true,
    name: true,
    type: true,
    amount: true,
    ifsc_code: true,
    status: true,
    transaction_id: true,
    transaction_date: true,
    pan_number: true,
    account_number: true,
    mobile: true,
    city: true,
    referal_amount: true,
  });

  // State for pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [goToPage, setGoToPage] = useState('');
  const [itemsPerPage, setItemsPerPage] = useState(20);
  
  const [approvalModalState, setApprovalModalState] = useState<{
    isOpen: boolean;
    transaction: Transaction | null;
    refId: string;
  }>({ isOpen: false, transaction: null, refId: '' });

  const [isAddTxModalOpen, setIsAddTxModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newTxData, setNewTxData] = useState({
    username: '',
    type: 'credit' as 'credit' | 'debit_withdrawal',
    amount: '',
    refId: '',
  });

  // --- Server-side Pagination State ---
  const [totalTransactions, setTotalTransactions] = useState(0);
  const [summary, setSummary] = useState({
    totalCredit: 0,
    totalDebit: 0,
    pendingCredit: 0,
    pendingDebit: 0,
  });

  // Ref to track initial mount and prevent double-fetch
  const isInitialMount = useRef(true);

  useEffect(() => {
    if (viewMode === 'gst') {
      const fetchGst = async () => {
        setIsLoading(true);
        setError(null);
        const params = new URLSearchParams();
        params.append('page', String(currentPage));
        params.append('limit', String(itemsPerPage));
        if (searchTerm) params.append('searchTerm', searchTerm);
        if (startDate) params.append('startDate', startDate);
        if (endDate) params.append('endDate', endDate);
        if (sortConfig) {
          params.append('sortKey', sortConfig.key as string);
          params.append('sortDirection', sortConfig.direction);
        }

        try {
          const response = await fetch(`/api/admin/transactions/gst?${params.toString()}`, { credentials: 'include' });
          if (!response.ok) {
            const data = await response.json();
            throw new Error(data.message || 'Failed to fetch GST data.');
          }
          const data = await response.json();
          const numericData = data.data.map((d: any) => ({
            ...d,
            id: Number(d.id),
            amount: Number(d.amount),
            quantity: Number(d.quantity),
            gst: Number(d.gst),
            invoice_number: Number(d.invoice_number),
          }));
          setGstData(numericData);
          setTotalTransactions(data.totalCount || 0);
        } catch (err) {
          setError(err instanceof Error ? err.message : 'An unknown error occurred.');
        } finally {
          setIsLoading(false);
        }
      };
      fetchGst();
      return;
    }
    if (viewMode === 'tds') {
      const fetchTds = async () => {
        setIsLoading(true);
        setError(null);
        const params = new URLSearchParams();
        params.append('page', String(currentPage));
        params.append('limit', String(itemsPerPage));
        if (searchTerm) params.append('searchTerm', searchTerm);
        if (startDate) params.append('startDate', startDate);
        if (endDate) params.append('endDate', endDate);
        if (sortConfig) {
          params.append('sortKey', sortConfig.key as string);
          params.append('sortDirection', sortConfig.direction);
        }

        try {
          const response = await fetch(`/api/admin/transactions/tds?${params.toString()}`, { credentials: 'include' });
          if (!response.ok) {
            const data = await response.json();
            throw new Error(data.message || 'Failed to fetch TDS data.');
          }
          const data = await response.json();
          const numericData = data.data.map((d: any) => ({
            ...d,
            id: Number(d.id),
            amount: Number(d.amount),
            tds: Number(d.tds),
          }));
          setTdsData(numericData);
          setTotalTransactions(data.totalCount || 0);
        } catch (err) {
          setError(err instanceof Error ? err.message : 'An unknown error occurred.');
        } finally {
          setIsLoading(false);
        }
      };
      fetchTds();
      return;
    }
    if (viewMode === 'consolidated') {
      const fetchConsolidated = async () => {
        setIsLoading(true);
        setError(null);
        const params = new URLSearchParams();
        params.append('page', String(currentPage));
        params.append('limit', String(itemsPerPage));
        if (searchTerm) params.append('searchTerm', searchTerm);
        if (startDate) params.append('startDate', startDate);
        if (endDate) params.append('endDate', endDate);
        if (sortConfig) {
          params.append('sortKey', sortConfig.key as string);
          params.append('sortDirection', sortConfig.direction);
        }

        try {
          const response = await fetch(`/api/admin/transactions/consolidated?${params.toString()}`, { credentials: 'include' });
          if (!response.ok) {
            const data = await response.json();
            throw new Error(data.message || 'Failed to fetch consolidated transactions.');
          }
          const data = await response.json();
          // Ensure all aggregated values are numbers before setting state
          const numericTransactions = data.transactions.map((t: any) => ({
            ...t,
            credit: Number(t.credit),
            debit_withdrawal: Number(t.debit_withdrawal),
            debit_direct_id_creation: Number(t.debit_direct_id_creation),
            debit_self_id_creation: Number(t.debit_self_id_creation),
            credit_autofill: Number(t.credit_autofill),
            admin_fee: Number(t.admin_fee),
            credit_direct_id: Number(t.credit_direct_id),
            credit_direct_self_id: Number(t.credit_direct_self_id),
          }));
          setConsolidatedTransactions(numericTransactions);
          setTotalTransactions(data.totalCount || 0);
        } catch (err) {
          setError(err instanceof Error ? err.message : 'An unknown error occurred.');
        } finally {
          setIsLoading(false);
        }
      };
      fetchConsolidated();
      return;
    }

    // Individual view logic
    const fetchServerSideTransactions = async () => {
      setIsLoading(true);
      setError(null);

      const params = new URLSearchParams();
      params.append('page', String(currentPage));
      params.append('limit', String(itemsPerPage));
      if (sortConfig) {
        params.append('sortKey', sortConfig.key);
        params.append('sortDirection', sortConfig.direction);
      }
      if (searchTerm) {
        params.append('searchTerm', searchTerm);
        const activeSearchCols = SEARCHABLE_TRANSACTION_KEYS.filter(key => searchableColumns[key]);
        if (activeSearchCols.length > 0) {
          params.append('searchableColumns', activeSearchCols.join(','));
        }
      }
      if (typeFilter !== 'all') params.append('type', typeFilter);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      try {
        const response = await fetch(`/api/admin/transactions?${params.toString()}`, { credentials: 'include' });
        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.message || 'Failed to fetch transactions.');
        }
        const data = await response.json();
        setTransactions(data.transactions);
        setTotalTransactions(data.totalCount);
        setSummary(data.summary);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An unknown error occurred.');
      } finally {
        setIsLoading(false);
      }
    };
    fetchServerSideTransactions();
  }, [currentPage, itemsPerPage, sortConfig, searchTerm, searchableColumns, typeFilter, statusFilter, startDate, endDate, viewMode]);

  const handleUpdateStatus = async (id: number, newStatus: 'completed' | 'failed', transactionId?: string) => {
    setActionError(null);
    try {
      const body: { status: 'completed' | 'failed', transactionId?: string } = { status: newStatus };
      if (newStatus === 'completed' && transactionId) {
        body.transactionId = transactionId;
      }
      const response = await fetch(`/api/admin/transactions/${id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message);
      }
      // Refresh the list to show the change
      // A full refetch is needed to update summary and list
      setTransactions(prev => prev.map(t => t.id === id ? { ...t, status: newStatus, transaction_id: transactionId || t.transaction_id } : t));
      // To avoid a full page reload, we can just update the one item.
      // For a full data refresh including summary, you would call fetchServerSideTransactions() again.
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'An unknown error occurred.');
    }
  };

  const openApprovalModal = (transaction: Transaction) => {
    // Only show modal for withdrawals, as they are the ones needing a ref ID from an external payment.
    if (transaction.type === 'debit_withdrawal') {
      setApprovalModalState({ isOpen: true, transaction, refId: '' });
    } else {
      // For credits or other types, approve directly without a ref ID modal.
      handleUpdateStatus(transaction.id, 'completed');
    }
  };

  const closeApprovalModal = () => {
    setApprovalModalState({ isOpen: false, transaction: null, refId: '' });
    setActionError(null); // Clear any modal-specific errors
  };

  const handleConfirmApproval = () => {
    if (!approvalModalState.transaction) return;
    if (approvalModalState.transaction.type === 'debit_withdrawal' && !approvalModalState.refId.trim()) {
      setActionError('A reference ID is required to approve a withdrawal.');
      return;
    }
    if (approvalModalState.transaction.type === 'debit_withdrawal' && !/^\d{12}$/.test(approvalModalState.refId)) {
      setActionError('UTR Number must be exactly 12 digits.');
      return;
    }
    handleUpdateStatus(approvalModalState.transaction.id, 'completed', approvalModalState.refId);
    // Optimistically close modal
    closeApprovalModal();
  };

  const handleCreateTransaction = async () => {
    setActionError(null);
    const amount = Number(newTxData.amount);
    if (!newTxData.username.trim() || !newTxData.refId.trim() || !amount || amount <= 0) {
      setActionError('Username, a valid positive amount, and a reference ID are required.');
      return;
    }    

    setIsSubmitting(true);
    try {
      const response = await fetch(`/api/admin/transactions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          username: newTxData.username,
          type: newTxData.type,
          amount: amount,
          transactionId: newTxData.refId,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message);
      }
      // Success
      setIsAddTxModalOpen(false);
      setNewTxData({ username: '', type: 'credit', amount: '', refId: '' });
      // To refresh the list while respecting filters, we can trigger the main useEffect.
      // Setting the page to 1 is a good way to do this, as it shows the user the newest data.
      if (currentPage !== 1) {
        setCurrentPage(1);
      }
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'An unknown error occurred.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSearchableColumnChange = (key: keyof typeof searchableColumns) => {
    setSearchableColumns(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const totalPages = itemsPerPage > 0 ? Math.ceil(totalTransactions / itemsPerPage) : 1;

  const displayedTransactions = transactions;

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleGoToPageSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const page = parseInt(goToPage, 10);
    if (!isNaN(page)) {
      handlePageChange(page);
    }
    setGoToPage('');
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchTerm(searchInput);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearchTerm('');
  };

  const requestSort = (key: SortableKeys | ConsolidatedSortableKeys | GstSortableKeys | TdsSortableKeys) => {
    let direction: 'ascending' | 'descending' = 'ascending';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const handleDownloadTds = async (transactionId: number) => {
    setActionError(null);
    try {
      const response = await fetch(`/api/admin/transactions/${transactionId}/tds-certificate`, {
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to download TDS certificate.');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `TDS_Certificate_${transactionId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'An unknown error occurred.');
    }
  };

  const handleDownloadGstPdf = async (transaction: Transaction) => {
    try {
      const response = await fetch(`/api/admin/transactions/${transaction.id}/invoice`, {
        method: 'GET',
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to generate invoice PDF.' }));
        throw new Error(errorData.message);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Invoice-${transaction.id}-${transaction.username}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred.';
      setActionError(errorMessage);
    }
  };

  const handleDownloadPdf = () => {
    const doc = new jsPDF({ orientation: 'landscape' });
    let tableColumn: string[] = [];
    let tableRows: (string | number)[][] = [];
    let title = "Transaction Report";
    let fileName = `transactions_${new Date().toISOString().slice(0,10)}.pdf`;

    if (viewMode === 'individual') {
      title = "Individual Transaction Report";
      tableColumn = ["S.No.", "ID", "Date", "Username", "Name", "Type", "Amount", "Ref ID", "Status"];
      displayedTransactions.forEach((t, index) => {
        let amountText = '';
        if (t.type.startsWith('credit') || t.type === 'admin_fee') {
          amountText = `+${t.amount.toFixed(2)}`;
        } else {
          amountText = `-${t.amount.toFixed(2)}`;
        }
        const row = [
          (currentPage - 1) * itemsPerPage + index + 1,
          t.id,
          new Date(t.transaction_date).toLocaleString(),
          t.username,
          t.name || '',
          getDisplayType(t.type),
          amountText,
          t.transaction_id || 'N/A',
          t.status
        ];
        tableRows.push(row);
      });
    } else if (viewMode === 'consolidated') {
      title = "Consolidated Transaction Report";
      fileName = `consolidated_transactions_${new Date().toISOString().slice(0,10)}.pdf`;
      tableColumn = [
        "S.No.", "Date", "Username", "Name", "Credit", "Withdrawal", 
        "Direct ID Creation", "Self ID Creation", "Direct ID Credit", 
        "Self ID Credit", "Autofill Credit", "Admin Fee"
      ];
      consolidatedTransactions.forEach((row, index) => {
        const r = [
          (currentPage - 1) * itemsPerPage + index + 1,
          row.transaction_date ? new Date(row.transaction_date).toLocaleString() : 'N/A',
          row.username,
          row.name || '',
          row.credit > 0 ? `+${row.credit.toFixed(2)}` : '-',
          row.debit_withdrawal > 0 ? `-${row.debit_withdrawal.toFixed(2)}` : '-',
          row.debit_direct_id_creation > 0 ? `-${row.debit_direct_id_creation.toFixed(2)}` : '-',
          row.debit_self_id_creation > 0 ? `-${row.debit_self_id_creation.toFixed(2)}` : '-',
          row.credit_direct_id > 0 ? `+${row.credit_direct_id.toFixed(2)}` : '-',
          row.credit_direct_self_id > 0 ? `+${row.credit_direct_self_id.toFixed(2)}` : '-',
          row.credit_autofill > 0 ? `+${row.credit_autofill.toFixed(2)}` : '-',
          row.admin_fee > 0 ? `+${row.admin_fee.toFixed(2)}` : '-'
        ];
        tableRows.push(r);
      });
    } else if (viewMode === 'gst') {
      title = "GST Report";
      fileName = `gst_report_${new Date().toISOString().slice(0,10)}.pdf`;
      tableColumn = ["S.No.", "Invoice No", "Date", "Username", "Name", "Quantity", "Amount", "GST"];
      gstData.forEach((row, index) => {
        const r = [
          (currentPage - 1) * itemsPerPage + index + 1,
          row.invoice_number,
          new Date(row.transaction_date).toLocaleString(),
          row.username,
          row.name || '',
          row.quantity,
          row.amount > 0 ? `${row.amount.toFixed(2)}` : '-',
          row.gst > 0 ? `${row.gst.toFixed(2)}` : '-'
        ];
        tableRows.push(r);
      });
    } else if (viewMode === 'tds') {
      title = "TDS Report";
      fileName = `tds_report_${new Date().toISOString().slice(0,10)}.pdf`;
      tableColumn = ["S.No.", "Date", "Username", "Name", "PAN", "Bank Name", "Account No", "IFSC", "Withdrawal", "TDS"];
      tdsData.forEach((row, index) => {
        const r = [
          (currentPage - 1) * itemsPerPage + index + 1,
          new Date(row.transaction_date).toLocaleString(),
          row.username,
          row.name || '',
          row.pan_number || 'N/A',
          row.bank_name || 'N/A',
          row.account_number || 'N/A',
          row.ifsc_code || 'N/A',
          row.amount > 0 ? `${row.amount.toFixed(2)}` : '-',
          row.tds > 0 ? `${row.tds.toFixed(2)}` : '-'
        ];
        tableRows.push(r);
      });
    }

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 20,
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [41, 128, 185], textColor: 255 },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      didDrawPage: function (data: HookData) {
        // Header
        doc.setFontSize(20);
        doc.setTextColor(40);
        doc.text(title, data.settings.margin.left, 15);
      },
    });
    doc.save(fileName);
  };

  const getSortIndicator = (name: SortableKeys | ConsolidatedSortableKeys | GstSortableKeys | TdsSortableKeys) => {
    if (!sortConfig || sortConfig.key !== name) return <span className="sort-indicator" />;
    return <span className="sort-indicator">{sortConfig.direction === 'ascending' ? '▲' : '▼'}</span>;
  };

  if (isLoading) return <div className="transaction-management-container"><h2>Transaction Management</h2><p>Loading...</p></div>;
  if (error) return <div className="transaction-management-container"><h2>Transaction Management</h2><div className="error-message">{error}</div></div>;

  return (
    <div className="transaction-management-container">
      {/* Component-specific styles for transaction type badges */}
      <style>{`
        .type-badge.type-admin_fee {
          background-color: #8884d8; /* A purple color similar to joining fee */
          color: white;
          border: 1px solid #7a75c3;
        }
        .type-badge.type-debit_direct_id_creation {
          background-color: #ff9800; /* Orange */
        }
        .type-badge.type-debit_self_id_creation {
          background-color: #d9534f; /* Soft Red */
        }
        .type-badge.type-credit_direct_id {
          background-color: #007bff; /* Blue */
          color: white;
        }
        .type-badge.type-credit_direct_self_id {
          background-color: #6f42c1; /* Purple */
          color: white;
        }
      `}</style>
      {/* Component-specific styles for responsive toolbar */}
      <style>{`
        @media (max-width: 768px) {
          .toolbar {
            flex-direction: column;
            align-items: stretch;
          }
          .summary-box {
            flex-direction: column;
          }
        }
      `}</style>
      {approvalModalState.isOpen && approvalModalState.transaction && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Approve Withdrawal</h3>
            <p>
              Approving withdrawal of <strong>₹{approvalModalState.transaction.amount.toFixed(2)}</strong> for user "<strong>{approvalModalState.transaction.username}</strong>".
            </p>
            <div className="transaction-user-details">
              <p><strong>Account Number:</strong> {approvalModalState.transaction.account_number || 'N/A'}</p>
              <p><strong>IFSC Code:</strong> {approvalModalState.transaction.ifsc_code || 'N/A'}</p>
            </div>
            <style>{`
              .transaction-user-details { background-color: #f8f9fa; border: 1px solid #dee2e6; border-radius: 4px; padding: 10px; margin: 15px 0; font-size: 0.9rem; }
              .transaction-user-details p { margin: 5px 0; }
            `}</style>
            <div className="modal-form-group">
              <label htmlFor="refId">Enter UTR Number</label>
              <input
                type="text"
                id="refId"
                placeholder="Enter the 12-digit UTR for this payment"
                value={approvalModalState.refId}
                onChange={(e) => setApprovalModalState(s => ({ ...s, refId: e.target.value }))}
                className="modal-input"
                pattern="\d{12}"
                title="UTR Number must be 12 digits."
                autoFocus
                required
              />
            </div>
            {actionError && <div className="error-message modal-error">{actionError}</div>}
            <div className="modal-actions">
              <button onClick={closeApprovalModal} className="action-button cancel-button">Cancel</button>
              <button onClick={handleConfirmApproval} className="action-button approve-button">Confirm Approval</button>
            </div>
          </div>
        </div>
      )}
      {isAddTxModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Create New Transaction</h3>
            <div className="modal-form-group">
              <label htmlFor="txType">Transaction Type</label>
              <select
                id="txType"
                value={newTxData.type}
                onChange={(e) => setNewTxData(s => ({ ...s, type: e.target.value as 'credit' | 'debit_withdrawal' }))}
                className="modal-input"
              >
                <option value="credit">Credit (Add Fund)</option>
                <option value="debit_withdrawal">Withdrawal (Deduct Fund)</option>
              </select>
            </div>
            <div className="modal-form-group">
              <label htmlFor="txUsername">Username</label>
              <input
                type="text" id="txUsername" placeholder="Enter target username"
                value={newTxData.username}
                onChange={(e) => setNewTxData(s => ({ ...s, username: e.target.value }))}
                className="modal-input" autoFocus
              />
            </div>
            <div className="modal-form-group">
              <label htmlFor="txAmount">Amount</label>
              <input
                type="number" id="txAmount" placeholder="e.g., 500" min="1"
                value={newTxData.amount}
                onChange={(e) => setNewTxData(s => ({ ...s, amount: e.target.value }))}
                className="modal-input"
              />
            </div>
            <div className="modal-form-group">
              <label htmlFor="txRefId">Reference ID / Note</label>
              <input
                type="text" id="txRefId" placeholder="e.g., Manual bonus for Q1"
                value={newTxData.refId}
                onChange={(e) => setNewTxData(s => ({ ...s, refId: e.target.value }))}
                className="modal-input"
              />
            </div>
            {actionError && <div className="error-message modal-error">{actionError}</div>}
            <div className="modal-actions">
              <button onClick={() => { setIsAddTxModalOpen(false); setActionError(null); }} className="action-button cancel-button">Cancel</button>
              <button onClick={handleCreateTransaction} className="action-button approve-button" disabled={isSubmitting}>
                {isSubmitting ? 'Creating...' : 'Create Transaction'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="page-header">
        <h2>Transaction Management</h2>
        <button onClick={() => setIsAddTxModalOpen(true)} className="action-button new-tx-button">+ New Transaction</button>
        <div className="filter-group">
          <label htmlFor="viewModeSelect">View Mode:</label>
          <select
            id="viewModeSelect"
            value={viewMode}
            onChange={(e) => setViewMode(e.target.value as 'individual' | 'consolidated' | 'gst' | 'tds')}
            className="filter-select"
          >
            <option value="individual">Individual</option>
            <option value="consolidated">Consolidated</option>
            <option value="gst">GST</option>
            <option value="tds">TDS</option>
          </select>
        </div>
        <button onClick={handleDownloadPdf} className="action-button download-pdf-button">
          Download PDF
        </button>
      </div>
      {actionError && !isAddTxModalOpen && !approvalModalState.isOpen && <div className="error-message">{actionError}</div>}
      <div className="toolbar">
        {viewMode === 'individual' && (
          <div className="summary-box">
          <div className="summary-item">
            <span>Total Credit:</span>
            <span className="amount-credit">₹{summary.totalCredit.toFixed(2)}</span>
          </div>
          <div className="summary-item">
            <span>Total Withdrawal:</span>
            <span className="amount-debit">₹{summary.totalDebit.toFixed(2)}</span>
          </div>
          <div className="summary-item summary-net">
            <strong>Total Net:</strong>
            <strong>₹{(summary.totalCredit - summary.totalDebit).toFixed(2)}</strong>
          </div>
          <div className="summary-separator"></div>
          <div className="summary-item">
            <span>Pending Credit:</span>
            <span className="amount-credit">₹{summary.pendingCredit.toFixed(2)}</span>
          </div>
          <div className="summary-item">
            <span>Pending Withdrawal:</span>
            <span className="amount-debit">₹{summary.pendingDebit.toFixed(2)}</span>
          </div>
          </div>
        )}
        <div className="search-group">
          <form onSubmit={handleSearchSubmit} className="search-form transaction-management-search">
            <div className="search-input-wrapper">
              <input
                type="text"
                placeholder="Search in selected columns..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="search-input"
              />
              {searchInput && (
                <button type="button" onClick={handleClearSearch} className="clear-search-button">&times;</button>
              )}
            </div>
            <button type="submit" className="search-button">Search</button>
          </form>
        </div>
        {viewMode === 'individual' && (
          <>
            <div className="filter-group">
              <label htmlFor="statusFilter">Filter by Status:</label>
              <select
                id="statusFilter"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value as any);
                  setCurrentPage(1);
                }}
                className="filter-select"
              >
                <option value="all">All</option>
                <option value="pending">Pending</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
              </select>
            </div>
            <div className="filter-group">
              <label htmlFor="typeFilter">Filter by Type:</label>
              <select
                id="typeFilter"
                value={typeFilter}
                onChange={(e) => {
                  setTypeFilter(e.target.value as any);
                  setCurrentPage(1);
                }}
                className="filter-select"
              >
                <option value="all">All Types</option>
                <option value="credit">Credit</option>
                <option value="debit_withdrawal">Withdrawal</option>
                <option value="debit_direct_id_creation">Direct ID Creation</option>
                <option value="debit_self_id_creation">Self ID Creation</option>
                <option value="credit_autofill">Credit Autofill</option>
                <option value="credit_direct_id">Direct ID Credit</option>
                <option value="credit_direct_self_id">Self ID Credit</option>
                <option value="admin_fee">Admin Fee</option>
              </select>
            </div>
          </>
        )}
        <div className="filter-group">
          <label htmlFor="startDate">From:</label>
          <input
            type="date"
            id="startDate"
            value={startDate}
            onChange={(e) => {
              setStartDate(e.target.value);
              setCurrentPage(1);
            }}
            className="date-input"
          />
          <label htmlFor="endDate">To:</label>
          <input
            type="date"
            id="endDate"
            value={endDate}
            onChange={(e) => {
              setEndDate(e.target.value);
              setCurrentPage(1);
            }}
            className="date-input"
          />
        </div>
      </div>

      {viewMode === 'individual' ? (
        <>
          <table className="admin-transaction-table">
            <thead>
              <tr>
                <th className="sno-column">S.No.</th>
                <th onClick={() => requestSort('id')} className="sortable-header">
                  <div className="th-content">
                    <span className="header-text">ID {getSortIndicator('id')}</span>
                  </div>
                </th>
                <th onClick={() => requestSort('transaction_date')} className="sortable-header">
                  <div className="th-content">
                    <span className="header-text">Date {getSortIndicator('transaction_date')}</span>
                  </div>
                </th>
                <th onClick={() => requestSort('username')} className="sortable-header">
                  <div className="th-content">
                    <span className="header-text">Username {getSortIndicator('username')}</span>
                    <label className="header-search-label">
                      <input type="checkbox" checked={searchableColumns.username} onChange={() => handleSearchableColumnChange('username')} onClick={(e) => e.stopPropagation()} />
                      Search
                    </label>
                  </div>
                </th>
                <th onClick={() => requestSort('name')} className="sortable-header">
                  <div className="th-content">
                    <span className="header-text">Name {getSortIndicator('name')}</span>
                    <label className="header-search-label">
                      <input type="checkbox" checked={searchableColumns.name} onChange={() => handleSearchableColumnChange('name')} onClick={(e) => e.stopPropagation()} />
                      Search
                    </label>
                  </div>
                </th>
                <th onClick={() => requestSort('type')} className="sortable-header">
                  <div className="th-content">
                    <span className="header-text">Type {getSortIndicator('type')}</span>
                  </div>
                </th>
                <th onClick={() => requestSort('amount')} className="sortable-header">
                  <div className="th-content">
                    <span className="header-text">Amount {getSortIndicator('amount')}</span>
                    <label className="header-search-label">
                      <input type="checkbox" checked={searchableColumns.amount} onChange={() => handleSearchableColumnChange('amount')} onClick={(e) => e.stopPropagation()} />
                      Search
                    </label>
                  </div>
                </th>
                <th onClick={() => requestSort('transaction_id')} className="sortable-header">
                  <div className="th-content">
                    <span className="header-text">Ref ID {getSortIndicator('transaction_id')}</span>
                    <label className="header-search-label">
                      <input type="checkbox" checked={searchableColumns.transaction_id} onChange={() => handleSearchableColumnChange('transaction_id')} onClick={(e) => e.stopPropagation()} />
                      Search
                    </label>
                  </div>
                </th>
                <th onClick={() => requestSort('status')} className="sortable-header">
                  <div className="th-content">
                    <span className="header-text">Status {getSortIndicator('status')}</span>
                  </div>
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayedTransactions.length > 0 ? (
                displayedTransactions.map((t, index) => (
                  <tr key={t.id}>
                    <td data-label="S.No." className="sno-column">{(currentPage - 1) * itemsPerPage + index + 1}</td>
                    <td data-label="ID">{t.id}</td>
                    <td data-label="Date">{new Date(t.transaction_date).toLocaleString()}</td>
                    <td data-label="Username">{t.username}</td>
                    <td data-label="Name">{t.name || '(No Name)'}</td>
                    <td data-label="Type"><span className={`type-badge type-${t.type}`}>{getDisplayType(t.type)}</span></td>
                    <td data-label="Amount" className={`transaction-amount ${t.type.startsWith('credit') || t.type === 'admin_fee' ? 'amount-credit' : 'amount-debit'}`}>
                      {t.type.startsWith('credit') || t.type === 'admin_fee' ? (
                        `+₹${t.amount.toFixed(2)}`
                      ) : t.type === 'debit_withdrawal' ? (
                        <>
                          <div>-₹{t.amount.toFixed(2)}</div>
                          <div className="fee-details">
                            <span className="mobile-only-fee">Fee (15%): -₹{(t.amount * 0.15).toFixed(2)}</span>
                            <strong>User Received: ₹{(t.amount * 0.85).toFixed(2)}</strong>
                          </div>
                        </>
                      ) : ( // debit_id_creation or other debits
                        `-₹${t.amount.toFixed(2)}` // debit_direct_id_creation or debit_self_id_creation
                      )}
                    </td>
                    <td data-label="Ref ID">
                      {t.transaction_id || 'N/A'}
                    </td>
                    <td data-label="Status">
                      <span className={`status-badge status-${t.status}`}>{t.status}</span>
                    </td>
                    <td data-label="Actions">
                      {t.status === 'pending' && (
                        <div className="action-buttons">
                          <button
                            onClick={() => openApprovalModal(t)}
                            className="action-button approve-button"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => handleUpdateStatus(t.id, 'failed')}
                            className="action-button reject-button"
                          >
                            Reject
                          </button>
                        </div>
                      )}
                      {t.status === 'completed' && (
                        <div className="action-buttons">
                            {t.type === 'debit_withdrawal' && (
                              <button
                                onClick={() => handleDownloadTds(t.id)}
                                className="action-button tds-button"
                              >TDS</button>
                            )}
                            {t.type === 'credit' && (
                              <button
                                onClick={() => handleDownloadGstPdf(t)}
                                className="action-button gst-button"
                              >GST</button>
                            )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={10} style={{ textAlign: 'center' }}>No transactions found for this filter.</td>
                </tr>
              )}
            </tbody>
          </table>
        </>
      ) : viewMode === 'consolidated' ? (
        <table className="admin-transaction-table consolidated-table">
          <thead>
            <tr>
              <th className="sno-column">S.No.</th>
              <th onClick={() => requestSort('transaction_date')} className="sortable-header">
                <div className="th-content">Date {getSortIndicator('transaction_date')}</div>
              </th>
              <th onClick={() => requestSort('username')} className="sortable-header">
                <div className="th-content">Username {getSortIndicator('username')}</div>
              </th>
              <th onClick={() => requestSort('name')} className="sortable-header">
                <div className="th-content">Name {getSortIndicator('name')}</div>
              </th>
              <th onClick={() => requestSort('credit')} className="sortable-header">
                <div className="th-content">Credit {getSortIndicator('credit')}</div>
              </th>
              <th onClick={() => requestSort('debit_withdrawal')} className="sortable-header">
                <div className="th-content">Withdrawal {getSortIndicator('debit_withdrawal')}</div>
              </th>
              <th onClick={() => requestSort('debit_direct_id_creation')} className="sortable-header">
                <div className="th-content">Direct ID Creation {getSortIndicator('debit_direct_id_creation')}</div>
              </th>
              <th onClick={() => requestSort('debit_self_id_creation')} className="sortable-header">
                <div className="th-content">Self ID Creation {getSortIndicator('debit_self_id_creation')}</div>
              </th>
              <th onClick={() => requestSort('credit_direct_id')} className="sortable-header">
                <div className="th-content">Direct ID Credit {getSortIndicator('credit_direct_id')}</div>
              </th>
              <th onClick={() => requestSort('credit_direct_self_id')} className="sortable-header">
                <div className="th-content">Self ID Credit {getSortIndicator('credit_direct_self_id')}</div>
              </th>
              <th onClick={() => requestSort('credit_autofill')} className="sortable-header">
                <div className="th-content">Autofill Credit {getSortIndicator('credit_autofill')}</div>
              </th>
              <th onClick={() => requestSort('admin_fee')} className="sortable-header">
                <div className="th-content">Admin Fee {getSortIndicator('admin_fee')}</div>
              </th>
            </tr>
          </thead>
          <tbody>
            {consolidatedTransactions.length > 0 ? (
              consolidatedTransactions.map((row, index) => (
                <tr key={row.username + index}>
                  <td data-label="S.No." className="sno-column">{(currentPage - 1) * itemsPerPage + index + 1}</td>
                  <td data-label="Date">
                    {row.transaction_date ? new Date(row.transaction_date).toLocaleString() : 'N/A'}
                  </td>
                  <td data-label="Username">{row.username}</td>
                  <td data-label="Name">{row.name || '(No Name)'}</td>
                  <td data-label="Credit" className="amount-credit">{row.credit > 0 ? `₹${row.credit.toFixed(2)}` : '-'}</td>
                  <td data-label="Withdrawal" className="amount-debit">{row.debit_withdrawal > 0 ? `₹${row.debit_withdrawal.toFixed(2)}` : '-'}</td>
                  <td data-label="Direct ID Creation" className="amount-debit">{row.debit_direct_id_creation > 0 ? `₹${row.debit_direct_id_creation.toFixed(2)}` : '-'}</td>
                  <td data-label="Self ID Creation" className="amount-debit">{row.debit_self_id_creation > 0 ? `₹${row.debit_self_id_creation.toFixed(2)}` : '-'}</td>
                  <td data-label="Direct ID Credit" className="amount-credit">{row.credit_direct_id > 0 ? `₹${row.credit_direct_id.toFixed(2)}` : '-'}</td>
                  <td data-label="Self ID Credit" className="amount-credit">{row.credit_direct_self_id > 0 ? `₹${row.credit_direct_self_id.toFixed(2)}` : '-'}</td>
                  <td data-label="Autofill Credit" className="amount-credit">{row.credit_autofill > 0 ? `₹${row.credit_autofill.toFixed(2)}` : '-'}</td>
                  <td data-label="Admin Fee" className="amount-credit">{row.admin_fee > 0 ? `₹${row.admin_fee.toFixed(2)}` : '-'}</td>
                </tr>
              ))
            ) : (
              <tr><td colSpan={12} style={{ textAlign: 'center' }}>No consolidated data found.</td></tr>
            )}
          </tbody>
        </table>
      ) : viewMode === 'gst' ? (
        <table className="admin-transaction-table gst-tds-table">
          <thead>
            <tr>
              <th className="sno-column">S.No.</th>
              <th onClick={() => requestSort('invoice_number')} className="sortable-header">
                <div className="th-content">Invoice No {getSortIndicator('invoice_number')}</div>
              </th>
              <th onClick={() => requestSort('transaction_date')} className="sortable-header">
                <div className="th-content">Date {getSortIndicator('transaction_date')}</div>
              </th>
              <th onClick={() => requestSort('username')} className="sortable-header">
                <div className="th-content">Username {getSortIndicator('username')}</div>
              </th>
              <th onClick={() => requestSort('name')} className="sortable-header">
                <div className="th-content">Name {getSortIndicator('name')}</div>
              </th>
              <th onClick={() => requestSort('quantity')} className="sortable-header">
                <div className="th-content">Quantity {getSortIndicator('quantity')}</div>
              </th>
              <th onClick={() => requestSort('amount')} className="sortable-header">
                <div className="th-content">Amount {getSortIndicator('amount')}</div>
              </th>
              <th onClick={() => requestSort('gst')} className="sortable-header">
                <div className="th-content">GST {getSortIndicator('gst')}</div>
              </th>
            </tr>
          </thead>
          <tbody>
            {gstData.length > 0 ? (
              gstData.map((row, index) => (
                <tr key={row.id}>
                  <td data-label="S.No.">{(currentPage - 1) * itemsPerPage + index + 1}</td>
                  <td data-label="Invoice No">{row.invoice_number}</td>
                  <td data-label="Date">{new Date(row.transaction_date).toLocaleString()}</td>
                  <td data-label="Username">{row.username}</td>
                  <td data-label="Name">{row.name || '(No Name)'}</td>
                  <td data-label="Quantity">{row.quantity}</td>
                  <td data-label="Amount" className="amount-credit">{row.amount > 0 ? `₹${row.amount.toFixed(2)}` : '-'}</td>
                  <td data-label="GST" className="amount-credit">{row.gst > 0 ? `₹${row.gst.toFixed(2)}` : '-'}</td>
                </tr>
              ))
            ) : (
              <tr><td colSpan={8} style={{ textAlign: 'center' }}>No GST data found.</td></tr>
            )}
          </tbody>
        </table>
      ) : ( // TDS View
        <table className="admin-transaction-table gst-tds-table">
          <thead>
            <tr>
              <th className="sno-column">S.No.</th>
               <th onClick={() => requestSort('transaction_date')} className="sortable-header">
                <div className="th-content">Date {getSortIndicator('transaction_date')}</div>
              </th>
              <th onClick={() => requestSort('username')} className="sortable-header">
                <div className="th-content">Username {getSortIndicator('username')}</div>
              </th>
              <th onClick={() => requestSort('name')} className="sortable-header">
                <div className="th-content">Name {getSortIndicator('name')}</div>
              </th>
              <th onClick={() => requestSort('pan_number')} className="sortable-header">
                <div className="th-content">PAN {getSortIndicator('pan_number')}</div>
              </th>
              <th onClick={() => requestSort('bank_name')} className="sortable-header">
                <div className="th-content">Bank Name {getSortIndicator('bank_name')}</div>
              </th>
              <th onClick={() => requestSort('account_number')} className="sortable-header">
                <div className="th-content">Account No {getSortIndicator('account_number')}</div>
              </th>
              <th onClick={() => requestSort('ifsc_code')} className="sortable-header">
                <div className="th-content">IFSC Code {getSortIndicator('ifsc_code')}</div>
              </th>
              <th onClick={() => requestSort('amount')} className="sortable-header">
                <div className="th-content">Withdrawal Amount {getSortIndicator('amount')}</div>
              </th>
              <th onClick={() => requestSort('tds')} className="sortable-header">
                <div className="th-content">TDS Amount {getSortIndicator('tds')}</div>
              </th>
            </tr>
          </thead>
          <tbody>
            {tdsData.length > 0 ? (
              tdsData.map((row, index) => (
                <tr key={row.username}>
                  <td data-label="S.No.">{(currentPage - 1) * itemsPerPage + index + 1}</td>
                  <td data-label="Date">{new Date(row.transaction_date).toLocaleString()}</td>
                  <td data-label="Username">{row.username}</td>
                  <td data-label="Name">{row.name || '(No Name)'}</td>
                  <td data-label="PAN">{row.pan_number || 'N/A'}</td>
                  <td data-label="Bank Name">{row.bank_name || 'N/A'}</td>
                  <td data-label="Account No">{row.account_number || 'N/A'}</td>
                  <td data-label="IFSC Code">{row.ifsc_code || 'N/A'}</td>
                  <td data-label="Withdrawal Amount" className="amount-debit">{row.amount > 0 ? `₹${row.amount.toFixed(2)}` : '-'}</td>
                  <td data-label="TDS Amount" className="amount-debit">{row.tds > 0 ? `₹${row.tds.toFixed(2)}` : '-'}</td>
                </tr>
              ))
            ) : (
              <tr><td colSpan={10} style={{ textAlign: 'center' }}>No TDS data found.</td></tr>
            )}
          </tbody>
        </table>
      )}
      {totalTransactions > 0 && (
        <div className="pagination-controls">
          <button onClick={() => handlePageChange(1)} disabled={currentPage === 1}>
            &laquo; First
          </button>
          <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
            &lsaquo; Previous
          </button>
          <span className="page-info">Page {currentPage} of {totalPages}</span>
          <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>
            Next &rsaquo;
          </button>
          <button onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages}>
            Last &raquo;
          </button>
          <form onSubmit={handleGoToPageSubmit} className="go-to-page-form">
            <input
              type="number"
              value={goToPage}
              onChange={(e) => setGoToPage(e.target.value)}
              className="page-input"
              min="1"
              max={totalPages}
              placeholder="Page"
            />
            <button type="submit">Go</button>
          </form>
          <div className="items-per-page-selector">
            <span>Items per page:</span>
            <select value={itemsPerPage} onChange={e => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }} className="filter-select">
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={200}>200</option>
              <option value={500}>500</option>
              <option value={1000}>1000</option>
            </select>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransactionManagement;